/* eslint no-irregular-whitespace: ["error", {"skipRegExps": true}] */

import axios, {HttpStatusCode} from 'axios';
import {format} from 'date-fns';
import exifr from 'exifr';


import configureStore from '../reducks/store';
import {endRequest, startRequest} from '../reducks/common/actions';
import {postRequest} from './backend';

const reduxStore = configureStore();
const {dispatch} = reduxStore;

/** ファイル名最大長 */
const MAX_FILENAME_LEN = 256;

/**
 * @typedef CreateUploadUrlItem アップロード用URL生成情報。
 *
 * @property {string} key ファイルを一意に識別するためのキー
 * @property {string} contentType ファイルのContent-type
 * @property {string} suffix 登録するファイルのオリジナル拡張子
 * @property {string} blobSuffix Blob Storage登録時に付加する拡張子
 * @property {File} data アップロードするファイル
 */

/**
 * @typedef CreateUploadUrlInfo アップロード用URL生成情報。
 *
 * @property {string} key ファイルを一意に識別するためのキー
 * @property {CreateUploadUrlItem[]} items アップロード用URL生成情報一覧
 */

/**
 * @typedef BlobUploadInfo Blob Storage登録用情報
 *
 * @property {string} url Blob Storageアップロード用URL
 * @property {import('axios').AxiosHeaders} headers ヘッダ情報
 * @property {File} data アップロードするファイル
 * @property {string} name Blobストレージ上のファイル名
 */

/**
 * @typedef {'BFA'|'BFB'|'BFE'|'BFI'|'BFP'|'BFU'|'BFF'} BizKind 業務区分
 */

/**
 * 複数のUploadコンポーネントが保持する情報からの一括URL生成情報生成。
 *
 * @param {object.<string, UploadFileInfo[]>} selectFileInfoList 複数のUploadコンポーネント情報
 * @param {boolean} useFileName ファイル名を指定するかどうか
 * @param {object.<string, string[]>} customInfoMap カスタム拡張子情報
 * @return {CreateUploadUrlInfo[]} アップロード用URL生成情報一覧
 */
export function createUploadUrlInfoFromMultiSelectFiles(
    selectFileInfoList, useFileName, customInfoMap,
) {
  const results = [];
  for (const info of selectFileInfoList) {
    const result = createUploadUrlInfoFromSelectFiles(
        info.name, info.selectFiles, useFileName, customInfoMap[info.name]);
    results.push(result);
  }

  return results;
}

/**
 * SelectFilesからのアップロード用URL生成情報生成。
 *
 * UploadコンポーネントのfileSetHandlerで取得できる選択ファイル一覧から、
 * アップロードURL生成用の情報を生成する。
 *
 * @param {string} name Uploadコンポーネント名
 * @param {UploadFileInfo[]} selectFiles 選択ファイル一覧
 * @param {boolean} useFileName ファイル名を指定するかどうか
 * @param {string[]} customInfo カスタム拡張子情報
 * @return {CreateUploadUrlInfo} アップロード用URL生成情報
 */
export function createUploadUrlInfoFromSelectFiles(
    name, selectFiles, useFileName, customInfo) {
  const items = [];

  for (let i = 0; i < selectFiles.length; i++) {
    const selectFile = selectFiles[i];

    // ContentDocumentIdが設定されているものはアップロード済のためスキップ
    if (selectFile.contentDocumentId) {
      continue;
    }

    /** @type File */
    const file = selectFile.fileData;

    const item = {
      suffix: getFileExtension(file),
      blobSuffix: getBlobSuffix(customInfo, i),
      contentType: selectFile.contentType,
      data: file,
    };

    // ファイル名を指定する場合は追加
    if (useFileName) {
      item.displayFileName = getFileName(file);
    }

    items.push(item);
  }

  return {
    key: name,
    items: items,
  };
}

/**
 * ファイル拡張子の取得。
 *
 * @param {File} file Fileオブジェクト
 * @return {string} 拡張子
 */
function getFileExtension(file) {
  const fileName = file.name;
  const extension = fileName.substring(fileName.lastIndexOf('.') + 1, fileName.length) || '';
  return extension;
}

/**
 * ファイル拡張子の取得。
 *
 * @param {string[]} customInfo カスタム拡張子情報
 * @param {number} index ファイルのインデックス
 * @return {string} 拡張子
 */
function getBlobSuffix(customInfo, index) {
  const customExt = customInfo[index];
  return customExt;
}

/**
 * 拡張子なしファイル名の取得。
 *
 * @param {File} file
 * @return {string} 拡張子なしファイル名
 */
function getFileName(file) {
  const fullName = file.name;
  const name = fullName.substring(0, fullName.lastIndexOf('.'));
  return name;
}

/**
 * Blob Storageへのファイル登録。
 *
 * Blob StorageへのアップロードURL生成と、アップロードの一連の処理を行う。
 *
 * @param {BizKind} bizKind 業務区分
 * @param {CreateUploadUrlInfo[]} uploadInfoList アップロード用URL生成情報一覧
 * @return {Promise<object.<string, string>>} キーと、紐づくファイルのアップロード後Blob Storageファイル名。
 */
export async function fileUploadToStorage(bizKind, uploadInfoList) {
  // ファイルの情報からアップロード用URLを生成
  const uploadUrlInfoList = await _createUploadUrl(bizKind, uploadInfoList);

  if (uploadInfoList.length == 0) {
    return {};
  }

  // アップロード用URLにファイルをPUTする
  const results = await _putToTempStorage(uploadUrlInfoList);
  return results;
}

/**
 * アップロード用URL情報の生成。
 *
 * @param {BizKind} bizKind 業務区分
 * @param {CreateUploadUrlInfo[]} uploadInfoList
 * @return {Promise<object.<string, BlobUploadInfo[]>>} アップロード用URL情報
 */
async function _createUploadUrl(bizKind, uploadInfoList) {
  // Fileを一旦退避して、アップロードURL生成APIに渡さないようにする
  const fileBackup = {};
  for (const uploadInfo of uploadInfoList) {
    const fileList = [];
    for (const item of uploadInfo.items) {
      fileList.push(item.data);
      delete item.data;
    }

    fileBackup[uploadInfo.key] = fileList;
  }

  const body = {
    bizKind,
    uploadInfoList,
  };

  // ファイルアップロード用のURLを生成する
  const response = await postRequest(null, 'v1.0/bfz999999/createUploadUrl', body);

  if (response.status != HttpStatusCode.Ok) {
    const message = response.data?.message;
    throw new Error(message.text, {cause: message});
  }

  // 取得した情報に退避しておいたファイルFileを付加して返却
  const responseData = response.data;
  for (const name in responseData) {
    if (!responseData.hasOwnProperty(name)) {
      continue;
    }

    const itemList = responseData[name];
    for (let i = 0; i < itemList.length; i++) {
      const item = itemList[i];
      item.data = fileBackup[name][i];
    }
  }

  return responseData;
}

/**
 * Blob Storage テンポラリへのファイルアップロード。
 *
 * @param {object.<string, BlobUploadInfo[]>} uploadInfoMap アップロード情報
 * @return {Promise<Object.<string, string[]>>} キーと、紐づくファイルのアップロード後Blob Storageファイル名。
 */
async function _putToTempStorage(uploadInfoMap) {
  const axiosInstance = axios.create();
  // 通信中画像表示
  dispatch(startRequest());

  const uploadedFileMap = {};

  // 署名付きURLでファイルのアップロードを行う
  /** @type import('axios').AxiosPromise<any>[] */
  const uploadPromises = [];

  for (const key in uploadInfoMap) {
    if (uploadInfoMap.hasOwnProperty(key)) {
      const uploadedFileList = [];

      const uploadInfoList = uploadInfoMap[key];
      for (const uploadInfo of uploadInfoList) {
      /** @type import('axios').AxiosRequestConfig<File> */
        const upInfo = {
          method: 'PUT',
          url: uploadInfo.url,
          headers: uploadInfo.headers,
          data: uploadInfo.data,
        };

        // Blob Storageのファイル名を返却値に設定
        uploadedFileList.push(uploadInfo.name);

        uploadPromises.push(axiosInstance(upInfo));
      }

      uploadedFileMap[key] = uploadedFileList;
    }
  }

  // catchは呼び出し側で行う
  try {
    // 全てのアップロードが完了したら処理終了する
    await axios.all(uploadPromises);
    return uploadedFileMap;
  } finally {
    // 通信中画像非表示
    dispatch(endRequest());
  }
}

/**
 * ファイル名長チェック。
 * @param {File[]} addFiles 選択ファイル情報一覧
 * @return {MessageInfo} チェックエラー時はエラーメッセージ情報。チェックOK時はnull
 */
export function checkFileNameLength(addFiles) {
  for (const file of addFiles) {
    const fileName = file.name;
    if (fileName.length > MAX_FILENAME_LEN) {
      return {id: 'BFY000073E', values: MAX_FILENAME_LEN.toString()};
    }
  }

  return null;
}

/**
 * ファイルサイズ0バイトチェック。
 * @param {File[]} addFiles 選択ファイル情報一覧
 * @return {MessageInfo} チェックエラー時はエラーメッセージ情報。チェックOK時はnull
 */
export function checkFileSizeZero(addFiles) {
  for (const file of addFiles) {
    if (file.size == 0) {
      return {id: 'BFY000074E'};
    }
  }

  return null;
}

/**
 * FileのEXIFから撮影日を取得。
 *
 * @param {File} file ファイル
 * @return {Promise<Date|null>} 撮影日。EXIFに設定がない場合null
 */
export const getDateTimeOriginalFromExif = async (file) => {
  let exif = null;
  try {
    exif = await exifr.parse(file);
  } catch (err) {
    // EXIF取得失敗時も処理は継続
    console.warn(err);
  }

  if (!exif) {
    return null;
  }

  return exif.DateTimeOriginal;
};

/**
 * FileのEXIFから撮影日文字列を取得。
 *
 * @param {File} file ファイル
 * @return {Promise<string|null>} 撮影日。EXIFに設定がない場合null
 */
export const getShootingDateFromExif = async (file) => {
  const date = await getDateTimeOriginalFromExif(file);
  if (!date) {
    return null;
  }

  const dateStr = format(date, 'yyyy-MM-dd');
  return dateStr;
};

// TODO: 以下は不要のため使用箇所の削除が必要

/**
 * 同名ファイルチェック。
 * @param {File[]} addFiles 選択ファイル情報一覧
 * @param {UploadFileInfo[]} registedFiles 登録済ファイル情報一覧
 * @return {MessageInfo} チェックエラー時はエラーメッセージ情報。チェックOK時はnull
 * @deprecated
 */
export function checkSameName(addFiles, registedFiles) {
  // 既に登録済のファイル名一覧を生成
  const fileNameList = [];
  for (const file of registedFiles) {
    fileNameList.push(file.fileName);
  }

  // 選択ファイルと同名ファイルが存在しないかチェック
  for (const file of addFiles) {
    const fileName = file.name;
    if (fileNameList.includes(fileName)) {
      return {id: 'BFY000075E', values: [fileName]};
    }

    fileNameList.push(fileName);
  }

  return null;
}
